import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { SkipNavContent } from '@reach/skip-nav';
import { useLocation } from '@reach/router';
import useNavigate from '#/shared/hooks/useNavigate';
import { elementRefType } from '#/prop-types';
import useSidePanelToggle from '#/user/hooks/useSidePanelToggle';
import { mergeRefs } from '#/shared/utils/react';
import { NAV_HEIGHT_VALUE } from '#/shared/constants';
import ROUTES from '#/constants/routes';
import * as Styled from './styled';

function TripToolsLayout({
  side,
  overlay,
  underlay,
  overlayOpen,
  closeMapPopup,
  contentPanelRef,
}) {
  const { setSidePanelMaximized, sidePanelIsMinimized } = useSidePanelToggle();
  const { mobileMode } = useSelector(s => s.user);
  const { pathname } = useLocation();

  // when the pathname changes on desktop maximize the side panel
  useEffect(() => {
    if (!mobileMode) setSidePanelMaximized();
  }, [
    mobileMode,
    setSidePanelMaximized,
    // `pathname` triggers this effect on route changes
    pathname,
  ]);

  // when the app is put into mobile mode maximize the side panel
  useEffect(() => {
    if (mobileMode) setSidePanelMaximized();
  }, [mobileMode, setSidePanelMaximized]);

  const sidePanelRef = useRef();

  const [sidePanelBottom, setSidePanelBottom] = useState(0);

  useEffect(() => {
    setSidePanelBottom(
      sidePanelIsMinimized && sidePanelRef?.current
        ? `-${sidePanelRef.current.offsetHeight - NAV_HEIGHT_VALUE}px`
        : 0
    );

    const handleResize = () => {
      // Ensure that sidePanelRef.current exists before trying to access offsetHeight
      setSidePanelBottom(
        sidePanelIsMinimized && sidePanelRef?.current
          ? `-${sidePanelRef.current.offsetHeight - NAV_HEIGHT_VALUE}px`
          : 0
      );
    };
    // Attach the event listener
    window.addEventListener('resize', handleResize);

    // Clean up function
    return () => {
      // Remove the event listener when the component is unmounted
      window.removeEventListener('resize', handleResize);
    };
  }, [sidePanelIsMinimized]);

  const { hideMapOnMobile, maxMapOffsetMobile, mapDefaultScrollMobile } =
    useSelector(s => s.map);

  // use a layout effect to minimize flickering on scroll changes
  useLayoutEffect(() => {
    // manage scrolling for route changes on mobile: for map views
    // scroll map to default size, otherwise scroll to top
    if (mobileMode) {
      window.scrollTo(
        0,
        hideMapOnMobile || overlayOpen ? 0 : mapDefaultScrollMobile
      );
    }
  }, [
    mobileMode,
    hideMapOnMobile,
    overlayOpen,
    mapDefaultScrollMobile,
    // `pathname` triggers this effect on route changes
    pathname,
  ]);

  const { matchPath } = useNavigate();
  const isHome = matchPath([ROUTES.home]);

  return (
    <Styled.TripToolsOuter>
      <Styled.TripToolsInner>
        {underlay}
        {overlay && (
          <Styled.OverlayPanel>
            <SkipNavContent />
            {overlay}
          </Styled.OverlayPanel>
        )}
        <Styled.ContentPanel
          mapOffsetMobile={hideMapOnMobile ? 0 : maxMapOffsetMobile}
        >
          {side && (
            <Styled.SidePanelWrapper
              isHome={isHome}
              bottom={sidePanelBottom}
              minimized={sidePanelIsMinimized}
              ref={mergeRefs(contentPanelRef, sidePanelRef)}
              hideSidePanel={overlayOpen}
            >
              <Styled.SidePanel
                onClick={closeMapPopup}
                data-testid="styled-side-panel"
              >
                {side}
              </Styled.SidePanel>
            </Styled.SidePanelWrapper>
          )}
        </Styled.ContentPanel>
      </Styled.TripToolsInner>
    </Styled.TripToolsOuter>
  );
}

TripToolsLayout.propTypes = {
  /**
   * Content to be displayed at full viewport size in the background
   * Usually contains the map
   */
  underlay: PropTypes.node.isRequired,
  /** Content that is always layered above the map */
  overlay: PropTypes.node,
  /**
   * Sidebar content displayed above the map as a sidebar on desktop, underneath
   * the map on mobile
   */
  side: PropTypes.node,
  /** Whether an overlay is open on top of this layout */
  overlayOpen: PropTypes.bool,
  /** Function that when called will click all leaflet-popup close buttons in document */
  closeMapPopup: PropTypes.func.isRequired,
  /** Ref for the content side panel for desktop */
  contentPanelRef: elementRefType,
};

TripToolsLayout.defaultProps = {
  overlay: null,
  side: null,
  overlayOpen: false,
  contentPanelRef: undefined,
};

export default TripToolsLayout;
